import React, { useEffect, useState } from 'react';
import Modal from '../../components/Shell/Modal';
import useModal from '../../hooks/useModal';
import {
	CheckCircleIcon,
	ChevronRightIcon,
	ClockIcon,
	DocumentCheckIcon,
	EllipsisVerticalIcon,
	MinusCircleIcon,
	PlusCircleIcon,
	PlusIcon,
	WrenchScrewdriverIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import {
	TemplateBoolean,
	TemplateCheckbox,
	TemplateContact,
	TemplateCreateable,
	TemplateDate,
	TemplateDropdown,
	TemplateField,
	TemplateFile,
	TemplateReactSelect,
	TemplateTeamMembers,
} from '../../components/Attributes/Attributes_old';
import usePrevious from '../../hooks/usePrevious';
import useAsync from '../../hooks/useAsync';
import {
	getModuleCategories,
	getModuleFieldsBeta,
	updateModuleFieldActive,
} from '../../api/modules';
import Button from '../../components/Button';
import Attributes, { configurePayload } from '../../components/Attributes';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import { FIELD_OPTIONS } from '../../components/helpers/constants';
import { capitalize } from 'lodash';
import {
	capitalizeFirstLetter,
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';
import { useRequiredBeta } from '../../hooks/useRequired';
import PDFModal from '../PDFModal';
import { addContract } from '../../api';
import { getDocuments } from '../../api/documents';
import moment from 'moment';
import { NOTIFY_OPTIONS } from '../../constants';
import { AvatarGenerated } from '../../components/Avatar';
import { getTeam } from '../../api/company';
import ContractTasks from './ContractTasks';
import { Menu } from '@headlessui/react';
import GeneralStepContract from './steps/GeneralStepContract';
import AssetsStepContract from './steps/AssetsStepContract';
import VendorStepContract from './steps/VendorStepContract';
import AttributesStepContract from './steps/AttributesStepContract';
import { useNavigate } from 'react-router-dom';

const MODAL_NAME = 'contractAdd';
const DEFAULT_FORM = {};
const CONTRACT_MODULE_ID = process.env.REACT_APP_MODULE_ID_CONTRACT;
const SERVICE_MODULE_ID = process.env.REACT_APP_MODULE_ID_SERVICE;
const EVENT_MODULE_ID = process.env.REACT_APP_MODULE_ID_EVENT;

const DEFAULT_NOTIFICATION_FORM = { time: 30 };

const STATUSES = {
	complete: 'text-green-700 bg-green-50 ring-green-600/20',
	default: 'text-gray-600 bg-gray-50 ring-gray-500/10',
	pending: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20',
};

const AddContractBetaModal = () => {
	const navigate = useNavigate();
	const [currentContractStep, setCurrentContractStep] = useState(0);
	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({ taskSchedule: [] });
	const [taskForm, setTaskForm] = useState({});
	const [notificationForm, setNotificationForm] = useState(
		DEFAULT_NOTIFICATION_FORM
	);
	const [customAttributeForm, setCustomAttributeForm] = useState({});
	const [acceptedFiles, setAcceptedFiles] = useState(null);

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');

	const [isAddTaskModalOpen, setAddTaskModal] = useState(false);
	const [isAddCustomAttributesModalOpen, setAddCustomAttributesModal] =
		useState(false);

	const { open: options, setClose } = useModal(MODAL_NAME);
	const {
		open: isPDFModalOpen,
		setClose: onCloseUploadContractPdfModal,
		setOpen: onOpenUploadContractPdfModal,
	} = useModal('uploadContractPdfModal');

	const [currentStep, setStep] = useState(1);
	const { open: addCategoryOpen, setOpen: setAddCategoryOpen } =
		useModal('addCategoryModal');

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: CONTRACT_MODULE_ID, active: true },
	});

	const { execute: onSetFieldActive } = useAsync(updateModuleFieldActive, {
		immediate: false,
	});

	const { value: taskModuleFields, status: isLoadingTaskModuleFields } =
		useAsync(getModuleFieldsBeta, {
			defaultPayload: { id: SERVICE_MODULE_ID, active: true },
		});

	const {
		value: categories,
		execute: onRefreshCategories,
		status: isLoadingCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});
	const {
		value: documents,
		execute: onRefreshDocuments,
		status: isLoadingDocuments,
	} = useAsync(getDocuments, {
		immediate: false,
		defaultPayload: {
			pageSize: null,
		},
	});

	const {
		value: eventCategories,
		execute: onRefreshEventCategories,
		status: isLoadingEventCategories,
	} = useAsync(getModuleCategories, { immediate: false });

	const { execute: handleAddContract, status: addingContract } = useAsync(
		addContract,
		{ immediate: false }
	);

	const { value: teamMembers } = useAsync(getTeam);

	const prevManageAttributes = usePrevious(manageAttributesSettings);
	useEffect(() => {
		if (!manageAttributesSettings && prevManageAttributes) {
			onRefreshModuleFields(
				{ categoryId: form?.category?.value?.value },
				null,
				{ silent: true }
			);
		}
	}, [manageAttributesSettings]);

	const previousCategory = usePrevious(form?.category?.value?.value);
	useEffect(() => {
		if (
			form?.category?.value?.value &&
			previousCategory !== form?.category?.value?.value
		) {
			onRefreshModuleFields(
				{ categoryId: form?.category?.value?.value },
				null,
				{ silent: true }
			);
		}
	}, [form?.category?.value?.value]);

	const prevOptions = usePrevious(options);
	useEffect(() => {
		if (options && !prevOptions) {
			onRefreshCategories({
				id: CONTRACT_MODULE_ID,
			});

			if (options?.assetId) {
				setUpdateForm({
					...updatedForm,
					assets: { value: [{ value: options?.assetId }] },
				});
			}

			onRefreshDocuments();
		}

		if (!options && prevOptions) {
			setForm({});
			setUpdateForm({ taskSchedule: [] });
			setStep(1);
			setCurrentContractStep(0);
			setTaskForm({});
			setNotificationForm(DEFAULT_NOTIFICATION_FORM);
		}
	}, [options]);

	const prevIsAddTaskModalOpen = usePrevious(isAddTaskModalOpen);
	const prevTaskModuleFields = usePrevious(taskModuleFields);
	useEffect(() => {
		if (currentContractStep === 2) {
			onRefreshEventCategories({
				id: EVENT_MODULE_ID,
			});
		}
	}, [currentContractStep]);

	useEffect(() => {
		if (isAddTaskModalOpen && taskModuleFields) {
			let obj = {};
			taskModuleFields?.standard
				?.filter((field) => field?.defaultValue)
				?.map((field) => {
					obj[field?.key] = { fieldId: field?.id, value: field?.defaultValue };
				});

			setTaskForm({ ...taskForm, ...obj });
		}
	}, [taskModuleFields, isAddTaskModalOpen]);

	useEffect(() => {
		if (acceptedFiles?.length) {
			onOpenUploadContractPdfModal(true);
		}
	}, [acceptedFiles]);

	const previousCoverageStart = usePrevious(
		updatedForm['coverage-start']?.value
	);
	useEffect(() => {
		if (previousCoverageStart !== updatedForm['coverage-start']) {
			setUpdateForm({
				...updatedForm,
				'coverage-end': {
					...(updatedForm['coverage-end'] || {
						fieldId: moduleFields?.combined?.find(
							(o) => o.key === 'coverage-end' && o.isDefault
						)?.id,
					}),
					value: moment(updatedForm['coverage-start']?.value)
						.add(1, 'year')
						.subtract(1, 'day')
						.toDate(),
				},
			});
		}
	}, [updatedForm['coverage-start']?.value]);

	const SERVICE_CONTRACT_CATEGORY = categories?.find(
		(category) =>
			category?.key === 'default:servicecontract' && category.isDefault
	);

	const WARRANTY_CONTRACT_CATEGORY = categories?.find(
		(category) => category?.key === 'default:warranty' && category.isDefault
	);

	const CATEGORY_FIELD_ID = moduleFields?.combined?.find(
		(o) => o.key === 'category' && o.isDefault
	)?.id;

	const id = null;
	const skipModuleKeys = [
		...(id ? ['coverage-start', 'duration'] : ['category']),
		...(options?.assetId ? ['assets'] : []),
	];
	const skipTaskModuleKeys = [
		'name',
		'description',
		'assets',
		'defaultEventStatus',
	];

	const hasAllRequired = useRequiredBeta(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key)
	);

	const onAddContract = ({ files = [] }) => {
		// Remove empty task fields
		const taskSchedule = {};

		Object.keys(taskForm)
			.filter((categoryId) => taskForm[categoryId]?.value)
			.map((categoryId) => (taskSchedule[categoryId] = taskForm[categoryId]));

		const contractObject = {
			...configurePayload({ ...updatedForm, ...form }, moduleFields?.combined),
			...(form?.document ? { document: form?.document?.value } : {}),
			...{ taskSchedule, notification: notificationForm },
		};

		handleAddContract(
			{
				...(files?.length
					? {
							files: acceptedFiles,
							bucket: 'local-contracts',
							information: contractObject,
					  }
					: contractObject),
			},
			(response) => {
				setAcceptedFiles(null);
				onCloseUploadContractPdfModal();
				setClose();
				navigate(`/manage/contracts/${response?.id}`);
			},
			{
				showNotification: {
					success: 'Contract added',
					error: 'Something went wrong...',
				},
			}
		);
	};

	const steps = [
		{ name: 'General' },
		{ name: 'Attributes' },
		{ name: 'Assets' },
		{ name: 'Tasks' },
		{ name: 'Vendor' },
	];

	const moduleFieldsBreakdown = {
		general: [
			...(moduleFields?.standard?.filter(
				(field) =>
					field?.isDefault &&
					['name', 'coverage-start', 'coverage-end'].includes(field?.key)
			) || []),
		],
		assets: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['assets'].includes(field?.key)
		),
		vendor: moduleFields?.standard?.filter(
			(field) => field?.isDefault && ['vendor'].includes(field?.key)
		),
		customAttributes:
			moduleFields?.combined?.filter((field) => !field?.isDefault) || [],
	};

	const OCCURRENCES_OPTIONS = taskModuleFields?.standard?.find(
		(field) => field?.isDefault && field?.key === 'occurrences'
	)?.options?.options;

	return (
		<>
			<Modal
				title={
					currentStep === 1
						? 'Select contract type'
						: currentStep === 2
						? 'Upload PDF (optional)'
						: currentStep === 3
						? ''
						: ''
				}
				open={!!options}
				onClose={() => {
					setClose({ onClose: options?.onClose });
				}}
				size={currentStep === 3 ? '4xl' : undefined}
				preventBackdropClose
			>
				{/* 
				
					Step 1: Category Selection 
					
				*/}
				{currentStep === 1 && (
					<div className="grid grid-cols-2 gap-x-4 gap-y-4">
						<div
							onClick={() => {
								setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: SERVICE_CONTRACT_CATEGORY?.name,
											value: SERVICE_CONTRACT_CATEGORY?.id,
										},
									},
								});
								setStep(2);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<DocumentCheckIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Service Contract
						</div>
						<div
							onClick={() => {
								setForm({
									...form,
									category: {
										fieldId: CATEGORY_FIELD_ID,
										value: {
											label: WARRANTY_CONTRACT_CATEGORY?.name,
											value: WARRANTY_CONTRACT_CATEGORY?.id,
										},
									},
								});
								setStep(2);
							}}
							className="border border-gray-200 hover:border-gray-300 cursor-pointer rounded-lg text-xs h-36 flex justify-center items-center font-light text-gray-600 hover:text-gray-900 flex-col"
						>
							<WrenchScrewdriverIcon className="w-6 h-6 text-gray-600 hover:text-gray-900 mb-3" />
							Warranty
						</div>
						<div className="col-span-2">
							<div className="flex">
								<div className="flex-1">
									<TemplateReactSelect
										name={'or choose a custom contract type'}
										placeholder="Select a custom contract type"
										dataIndex={'categoryId'}
										options={
											categories
												?.filter((category) => !category?.isDefault)
												.map((category) => ({
													label: category?.name,
													value: category?.id,
												})) || []
										}
										value={form['categoryId'] || ''}
										onChange={(index, category) => {
											setForm({
												...form,
												category: {
													fieldId: CATEGORY_FIELD_ID,
													value: category,
												},
											});
											setStep(2);
										}}
										disabled={isLoadingCategories === 'pending'}
									/>
								</div>
								{form['categoryId']?.value ? (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<XCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() => {
												setForm({ ...form, category: null });
											}}
										/>
									</div>
								) : (
									<div className="flex-shrink flex items-center pt-7 ml-3">
										<PlusCircleIcon
											className="w-6 h-6 cursor-pointer text-gray-500"
											onClick={() =>
												setAddCategoryOpen({
													moduleId: CONTRACT_MODULE_ID,
													onSubmit: (category) => {
														setForm({
															...form,
															category: {
																fieldId: CATEGORY_FIELD_ID,
																value: category,
															},
														});
														setStep(2);
													},
												})
											}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* 
				
					Step 2: Document Selection 
					
				*/}
				{currentStep === 2 && (
					<div>
						<p className="text-xs text-gray-500 mb-4">
							If you would like to store the contract PDF provided by the
							vendor, feel free to upload it now. This is optional
						</p>
						<TemplateFile
							onChange={(index, acceptedFiles) => {
								setAcceptedFiles(acceptedFiles);
							}}
						/>

						<div className="col-span-2">
							<div className="flex">
								<div className="flex-1">
									<TemplateReactSelect
										name={'or choose an existing document'}
										placeholder="Select an existing document"
										dataIndex={'documentId'}
										options={
											documents?.rows.map((document) => ({
												label: document?.name,
												value: document?.id,
											})) || []
										}
										value={form['document'] || ''}
										onChange={(index, document) => {
											setForm({
												...form,
												document,
											});
											setStep(3);
										}}
										disabled={isLoadingCategories === 'pending'}
									/>
								</div>
							</div>
						</div>
						<div className="flex justify-end mt-4">
							<Button type="secondary" onClick={() => setStep(3)}>
								Skip
							</Button>
						</div>

						{/* 
				
							Step 2 1/2: PDF Modal Contract Details
					
						*/}

						<PDFModal
							setClose={() => {
								onCloseUploadContractPdfModal();
								setAcceptedFiles(null);
							}}
							options={{
								file: acceptedFiles?.length ? acceptedFiles[0] : null,
								actions: [
									{
										label: 'Add Contract',
										disabled: !hasAllRequired,
										onClick: () => onAddContract({ files: acceptedFiles }),
									},
								],
							}}
						>
							<div className="grid gap-y-4 pt-3">
								<>
									{isLoadingModuleFields === 'pending' ? (
										<LoadingSkeleton />
									) : (
										<>
											<div
												className={classNames(
													'grid gap-y-4 gap-x-4 text-left grid-cols-2'
												)}
											>
												{currentContractStep === 0 && (
													<>
														<GeneralStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>

														{/*moduleFieldsBreakdown?.general
															?.filter(
																(field) =>
																	skipModuleKeys.indexOf(field.key) === -1
															)
															?.map((field) => (
																<>
																	<Attributes
																		field={field}
																		onChange={(index, value, fullValue) => {
																			if (value || fullValue) {
																				let updatedValue = form[index]?.value;

																				if (fullValue) {
																					updatedValue = fullValue;
																				} else {
																					if (
																						updatedValue &&
																						Array.isArray(value)
																					) {
																						updatedValue = [
																							...(Array.isArray(updatedValue)
																								? updatedValue
																								: [updatedValue]),
																							...value,
																						];
																					} else {
																						updatedValue = value;
																					}
																				}

																				setUpdateForm({
																					...updatedForm,
																					[index]: {
																						value: updatedValue,
																						fieldId: field.id,
																					},
																				});
																			} else {
																				let updated = { ...updatedForm };
																				updated[index] = {
																					...updated[index],
																					value: null,
																				};

																				setUpdateForm(updated);
																			}
																		}}
																		form={{ ...form, ...updatedForm }}
																		updatedForm={updatedForm}
																		setForm={setUpdateForm}
																		moduleId={CONTRACT_MODULE_ID}
																	/>
																</>
															))*/}
													</>
												)}

												{/* 
				
														Step 2.12: Main Content - Attributes
														
													*/}
												{currentContractStep === 1 && (
													<>
														<AttributesStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													</>
												)}

												{/* 
				
														Step 2.13: Main Content - Assets
														
													*/}
												{currentContractStep === 2 && (
													<>
														<AssetsStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													</>
												)}

												{/* 
				
														Step 2.14: Main Content - Tasks
														
													*/}
												{currentContractStep === 3 && (
													<>
														<ContractTasks
															taskForm={taskForm}
															setTaskForm={setTaskForm}
															updatedForm={updatedForm}
														/>
													</>
												)}

												{/* 
				
														Step 2.13: Main Content - Vendors
														
													*/}
												{currentContractStep === 4 && (
													<>
														<VendorStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													</>
												)}
											</div>
										</>
									)}
								</>

								<nav
									aria-label="Progress"
									className="flex items-center justify-center mt-3"
								>
									<p className="text-sm font-medium">
										{`${currentContractStep + 1} `}
										of {steps.length}
									</p>
									<ol role="list" className="ml-8 flex items-center space-x-5">
										{steps.map((step, stepIdx) => (
											<li key={step.name}>
												{stepIdx < currentContractStep ? (
													<a
														href={step.href}
														className="block h-2.5 w-2.5 rounded-full bg-indigo-600 hover:bg-indigo-900"
													>
														<span className="sr-only">{step.name}</span>
													</a>
												) : stepIdx === currentContractStep ? (
													<a
														href={step.href}
														aria-current="step"
														className="relative flex items-center justify-center"
													>
														<span
															aria-hidden="true"
															className="absolute flex h-5 w-5 p-px"
														>
															<span className="h-full w-full rounded-full bg-indigo-200" />
														</span>
														<span
															aria-hidden="true"
															className="relative block h-2.5 w-2.5 rounded-full bg-indigo-600"
														/>
														<span className="sr-only">{step.name}</span>
													</a>
												) : (
													<a
														href={step.href}
														className="block h-2.5 w-2.5 rounded-full bg-gray-200 hover:bg-gray-400"
													>
														<span className="sr-only">{step.name}</span>
													</a>
												)}
											</li>
										))}
									</ol>
								</nav>

								<div className="flex justify-between mt-4">
									<Button
										type={currentContractStep === 0 ? 'secondary' : undefined}
										disabled={currentContractStep === 0}
										onClick={() =>
											setCurrentContractStep(currentContractStep - 1)
										}
									>
										Previous
									</Button>

									{currentContractStep + 1 !== steps?.length && (
										<Button
											onClick={() => {
												setCurrentContractStep(currentContractStep + 1);
											}}
											type="ghost"
										>
											{`Optional: Add ${steps[currentContractStep + 1]?.name}`}
										</Button>
									)}
								</div>
							</div>
						</PDFModal>
					</div>
				)}

				{/* 
				
					Step 3: Contract Details (Non-modal)
					
				*/}
				{currentStep === 3 && (
					<div className="grid grid-cols-12 gap-x-4">
						{/* 
				
							Step 3.1: Sidebar
							
						*/}
						<div className="col-span-2 my-5">
							<nav aria-label="Progress" className="flex">
								<ol role="list" className="space-y-6">
									{steps.map((step, stepIdx) => (
										<li key={step.name}>
											{stepIdx < currentContractStep ? (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											) : stepIdx === currentContractStep ? (
												<a
													href={step.href}
													aria-current="step"
													className="flex items-center cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<span
														aria-hidden="true"
														className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
													>
														<span className="absolute h-4 w-4 rounded-full bg-indigo-200" />
														<span className="relative block h-2 w-2 rounded-full bg-indigo-600" />
													</span>
													<span className="ml-3 text-xs font-medium text-indigo-600">
														{step.name}
													</span>
												</a>
											) : (
												<a
													href={step.href}
													className="group cursor-pointer"
													onClick={() => setCurrentContractStep(stepIdx)}
												>
													<div className="flex items-center">
														<div
															aria-hidden="true"
															className="relative flex h-5 w-5 flex-shrink-0 items-center justify-center"
														>
															<div className="h-2 w-2 rounded-full bg-gray-300 group-hover:bg-gray-400" />
														</div>
														<p className="ml-3 text-xs font-medium text-gray-500 group-hover:text-gray-900">
															{step.name}
														</p>
													</div>
												</a>
											)}
										</li>
									))}
								</ol>
							</nav>
						</div>

						{/* 
				
							Step 3.1: Main Content
							
						*/}
						<div className="col-span-10 pl-4">
							<div className="grid gap-y-4 pt-3">
								<>
									{isLoadingModuleFields === 'pending' ? (
										<LoadingSkeleton />
									) : (
										<>
											<div className="text-lg flex items-center justify-between">
												{steps[currentContractStep]?.name}
											</div>
											<div className=" min-h-72">
												<div
													className={classNames(
														'grid gap-y-4 gap-x-4 text-left',
														currentContractStep === 4 ||
															currentContractStep === 3
															? 'grid-cols-1'
															: 'grid-cols-2'
													)}
												>
													{/* 
				
														Step 3.11: Main Content - General Details
														
													*/}
													{currentContractStep === 0 && (
														<GeneralStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{/* 
				
														Step 3.12: Main Content - Attributes
														
													*/}
													{currentContractStep === 1 && (
														<>
															<AttributesStepContract
																moduleFieldsBreakdown={moduleFieldsBreakdown}
																skipModuleKeys={skipModuleKeys}
																form={form}
																setForm={setForm}
																updatedForm={updatedForm}
																setUpdateForm={setUpdateForm}
															/>
														</>
													)}

													{/* 
				
														Step 3.13: Main Content - Assets
														
													*/}

													{currentContractStep === 2 && (
														<AssetsStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}

													{/* 
				
														Step 3.14: Main Content - Tasks
														
													*/}
													{currentContractStep === 3 && (
														<>
															<div className="col-span-2 space-y-4">
																<ContractTasks
																	taskForm={taskForm}
																	setTaskForm={setTaskForm}
																	updatedForm={updatedForm}
																/>
															</div>
														</>
													)}

													{/* 
				
														Step 3.15: Main Content - Vendor Details
														
													*/}
													{currentContractStep === 4 && (
														<VendorStepContract
															moduleFieldsBreakdown={moduleFieldsBreakdown}
															skipModuleKeys={skipModuleKeys}
															form={form}
															setForm={setForm}
															updatedForm={updatedForm}
															setUpdateForm={setUpdateForm}
														/>
													)}
												</div>
											</div>
										</>
									)}
								</>

								<div className="flex justify-end mt-4">
									{currentContractStep + 1 !== steps?.length && (
										<Button
											onClick={() => {
												setCurrentContractStep(currentContractStep + 1);
											}}
											type="ghost"
										>
											{`Optional: Add ${steps[currentContractStep + 1]?.name}`}
										</Button>
									)}

									<Button
										disabled={!hasAllRequired}
										onClick={() => onAddContract({ files: acceptedFiles })}
									>
										Add Contract
									</Button>
								</div>
							</div>
						</div>
					</div>
				)}
			</Modal>

			<Modal
				title="Add scheduled task"
				open={isAddTaskModalOpen}
				onClose={() => setAddTaskModal(false)}
				size="xl"
			>
				<>
					{isLoadingTaskModuleFields === 'pending' ||
					isLoadingEventCategories === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<>
							<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
								{taskModuleFields?.standard
									?.filter(
										(field) => skipTaskModuleKeys.indexOf(field.key) === -1
									)
									?.filter((field) => field?.isDefault)
									?.map((field, fieldId) => {
										return (
											<>
												<Attributes
													key={fieldId}
													field={field}
													onChange={(index, value, fullValue) => {
														if (value || fullValue) {
															let updatedValue = taskForm[index]?.value;

															if (fullValue) {
																updatedValue = fullValue;
															} else {
																if (updatedValue && Array.isArray(value)) {
																	updatedValue = [
																		...(Array.isArray(updatedValue)
																			? updatedValue
																			: [updatedValue]),
																		...value,
																	];
																} else {
																	updatedValue = value;
																}
															}

															setTaskForm({
																...taskForm,
																[index]: {
																	value: updatedValue,
																	fieldId: field.id,
																},
															});
														} else {
															let updated = { ...taskForm };
															updated[index] = {
																...updated[index],
																value: null,
															};

															setTaskForm(updated);
														}
													}}
													form={{
														...(updatedForm?.serviceStart || {}),
														...(updatedForm?.taskSchedule || {}),
														...taskForm,
													}}
													updatedForm={taskForm}
													setForm={setTaskForm}
													moduleId={SERVICE_MODULE_ID}
												/>
											</>
										);
									})}
							</div>

							<div className="col-span-2 flex justify-end mt-8">
								<Button
									onClick={() => {
										let updatedTaskSchedule = updatedForm?.taskSchedule;

										updatedTaskSchedule?.push(taskForm);

										setAddTaskModal(false);
										setUpdateForm({
											...updatedForm,
											taskSchedule: updatedTaskSchedule,
										});
									}}
								>
									Add Task
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>

			<Modal
				title="Add custom field"
				open={isAddCustomAttributesModalOpen}
				onClose={() => setAddCustomAttributesModal(false)}
				size="xl"
			>
				<>
					{isLoadingTaskModuleFields === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<>
							<div className="grid grid-cols-1 gap-y-4 gap-x-4 text-left">
								<TemplateCreateable
									name="Attribute"
									placeholder="Select or type to create..."
									options={moduleFields?.standard
										?.filter((field) => !field?.isDefault)
										.map((field) => ({
											label: field?.label,
											value: field?.id,
										}))}
									onChange={(index, selectedCustomField, raw) => {
										if (raw) {
											setCustomAttributeForm({
												...customAttributeForm,
												...(raw?.__isNew__
													? { label: raw?.label }
													: { id: raw?.value }),
											});
										} else {
											setCustomAttributeForm({});
										}
									}}
									skip={[]}
									value={customAttributeForm?.label || customAttributeForm?.id}
								/>

								{customAttributeForm?.label ? (
									<div className="grid grid-cols-2 gap-y-4">
										<TemplateReactSelect
											name="Type"
											options={FIELD_OPTIONS}
											onChange={(index, type) =>
												setCustomAttributeForm({ ...customAttributeForm, type })
											}
											value={customAttributeForm?.type}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Required"
											className="mr-3"
											onChange={(index, required) =>
												setCustomAttributeForm({
													...customAttributeForm,
													required,
												})
											}
											value={customAttributeForm?.required}
										/>
										<TemplateBoolean
											span={'sm:col-span-1'}
											name="Searchable"
											onChange={(index, searchable) =>
												setCustomAttributeForm({
													...customAttributeForm,
													searchable,
												})
											}
											value={customAttributeForm?.searchable}
										/>

										<TemplateField
											span={'sm:col-span-2'}
											name="Value"
											className="mr-3"
											onChange={(index, value) =>
												setCustomAttributeForm({
													...customAttributeForm,
													value,
												})
											}
											value={customAttributeForm?.value}
										/>
									</div>
								) : null}
							</div>

							<div className="col-span-2 flex justify-end mt-8">
								<Button onClick={() => setAddCustomAttributesModal(false)}>
									Done
								</Button>
							</div>
						</>
					)}
				</>
			</Modal>
		</>
	);
};

export default AddContractBetaModal;
